'use client';

import Link from '@eliadmin/core/src/components/link';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col overflow-hidden', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full min-w-max flex-row items-center justify-between px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug} className="flex items-center">
          <SheetClose>
            <div
              className={cn(
                'h-6 text-xl font-bold',
                layer === 1 && 'text-[16px] font-semibold',
                layer && layer > 1 && 'text-sm font-normal',
              )}
            >
              {category.name}
            </div>
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger
            aria-label={category.name}
            className="h-5"
            iconstyles="w-5 h-5 text-primary"
          />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore((state) => state.generalLayout);

  const categories = props.categories || generalLayout.header?.categories;

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label={t('text3241')}
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={'right'}
          className={cn(
            'justify-between overflow-scroll p-0',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
          <div className=" px-3">
            <ul
              className={cn(
                'mb-4 overflow-scroll',
                'ea-menu-sidebar__list',
                // className?.list,
              )}
            >
              <Accordion type="single" collapsible>
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
            </ul>
            {!!props.menu && (
              <div className="flex flex-col gap-2 p-4">
                {props.menu?.map((item, index) => (
                  <SheetClose key={index} asChild>
                    <Link prefetchOnHover href={item.path || '/'} className="flex w-full">
                      <div className={cn('text-sm', '')}>{item.name}</div>
                    </Link>
                  </SheetClose>
                ))}
              </div>
            )}
            <div className="p-4">
              <Separator className="mb-3" />
              <div className={cn('', 'ea-header-main__language')}>
                <LanguageSelect />
              </div>
            </div>
            <div className="p-4 pt-0">
              <Separator className="mb-3" />
              <SocialIcons
                className={{
                  icon: 'h-5 w-5',
                  button: 'h-12 w-12',
                  wrapper: '',
                }}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
